import { z } from 'zod'

/** Stable review-reason codes stored in `members.review_reasons` (spec "Decision rules" 5, location triggers). */
export const REVIEW_REASONS = [
  'low_biometric_confidence',
  'provider_review',
  'provider_reject_other',
  'provider_injection_signal',
  'age_borderline',
  'age_under',
  'age_unverified',
  'face_borderline',
  'unblocked_retry',
  'evidence_missing',
  'repeated_failures',
  'liveness_failed',
  'ip_country_mismatch',
  'ip_far_from_city',
  'vpn_or_proxy',
  'gps_far_from_city',
  'location_unknown',
  // Location evidence v2 (docs/specs/2026-10-26-location-v2-design.md "Outcome").
  'device_location_far',
  'device_location_inaccurate',
  'device_location_stale',
  'device_location_missing',
  'timezone_mismatch',
  // Identity document (docs/specs/2026-10-27-identity-document-design.md §13).
  'document_review_required',
  'document_exempt',
  'document_missing',
  'document_not_genuine',
  'document_expired',
  'document_portrait_unusable',
  'document_face_uncertain',
  'document_face_mismatch',
  'document_name_disputed',
  'document_age_conflict',
  'document_integrity_missing',
  'document_policy_mismatch',
  'document_evidence_stale',
  'document_exemption_stale',
  'document_consent_withdrawn',
  'face_reference_uncertain',
  'face_reference_mismatch',
  'face_reference_missing',
  // Identity document (Task 21, ruling R25): a required check of an accept / review result is `failed`.
  'document_integrity_failed',
  // Face-first gate G3: a two-side document whose back the provider gave no signal about.
  'document_sides_unverified',
  // One person = one member: another member that is not erased settled with the same document (keyed index).
  'document_already_used',
] as const
export type ReviewReason = (typeof REVIEW_REASONS)[number]

/** Every location reason: v1 (`ip_far_from_city`, `gps_far_from_city`, `location_unknown`, kept to render old rows) and v2. */
export const LOCATION_REASONS = [
  'ip_country_mismatch',
  'ip_far_from_city',
  'vpn_or_proxy',
  'gps_far_from_city',
  'location_unknown',
  'device_location_far',
  'device_location_inaccurate',
  'device_location_stale',
  'device_location_missing',
  'timezone_mismatch',
] as const satisfies readonly ReviewReason[]
export type LocationReason = (typeof LOCATION_REASONS)[number]

/** Every document reason (spec §13). */
export const DOCUMENT_REVIEW_REASONS = [
  'document_review_required',
  'document_exempt',
  'document_missing',
  'document_not_genuine',
  'document_expired',
  'document_portrait_unusable',
  'document_face_uncertain',
  'document_face_mismatch',
  'document_name_disputed',
  'document_age_conflict',
  'document_integrity_missing',
  'document_policy_mismatch',
  'document_evidence_stale',
  'document_exemption_stale',
  'document_consent_withdrawn',
  'document_integrity_failed',
  'document_sides_unverified',
  'document_already_used',
] as const satisfies readonly ReviewReason[]
export type DocumentReviewReason = (typeof DOCUMENT_REVIEW_REASONS)[number]

/** Reasons raised by the immutable face-reference anchor (spec §13). */
export const ANCHOR_REVIEW_REASONS = [
  'face_reference_uncertain',
  'face_reference_mismatch',
  'face_reference_missing',
] as const satisfies readonly ReviewReason[]
export type AnchorReviewReason = (typeof ANCHOR_REVIEW_REASONS)[number]

/** Reasons that survive a retry: only an admin decision clears them (spec §13). */
export const STICKY_REVIEW_REASONS = [
  'document_not_genuine',
  'document_policy_mismatch',
  'document_face_mismatch',
  'face_reference_mismatch',
] as const satisfies readonly ReviewReason[]

export const REASON_CATEGORIES = ['age', 'place', 'face', 'document'] as const
export const ReasonCategorySchema = z.enum(REASON_CATEGORIES)
export type ReasonCategory = (typeof REASON_CATEGORIES)[number]

/** Plain-language category shown to the member (S6 "Why?", copy v6). */
export function reasonCategory(r: ReviewReason): ReasonCategory {
  if (r.startsWith('age_')) return 'age'
  if (r.startsWith('document_')) return 'document'
  if ((LOCATION_REASONS as readonly string[]).includes(r)) return 'place'
  return 'face'
}
